/**
 * Skill Library — Installed skills display with module inventory,
 * registration, activation toggle, and sync.
 * Phase 4.7 — WQ-164, enhanced WQ-172
 */
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { RefreshIcon } from '../common/Icons';
import { listSkills, registerSkill, updateSkill, syncSkills, type Skill } from '../../services/skillService';

const SkillLibrary: React.FC = () => {
  const { authFetch, isLoading: authLoading } = useAuth();
  const [skills, setSkills] = useState<Skill[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({
    name: '', slug: '', version: '1.0.0', description: '',
    install_path: '', module_count: 0,
  });
  const [actionLoading, setActionLoading] = useState(false);
  const [syncResult, setSyncResult] = useState<string | null>(null);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listSkills(authFetch, false);
      setSkills(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load skills');
    }
    setLoading(false);
  };

  useEffect(() => {
    if (!authLoading) fetchData();
  }, [authLoading]);

  const handleCreate = async () => {
    if (!createForm.name.trim() || !createForm.slug.trim()) return;
    setActionLoading(true);
    try {
      await registerSkill(authFetch, {
        name: createForm.name.trim(),
        slug: createForm.slug.trim(),
        version: createForm.version.trim(),
        description: createForm.description.trim() || null,
        install_path: createForm.install_path.trim(),
        module_count: createForm.module_count,
        module_inventory: null,
      });
      setShowCreate(false);
      setCreateForm({ name: '', slug: '', version: '1.0.0', description: '', install_path: '', module_count: 0 });
      await fetchData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Registration failed');
    }
    setActionLoading(false);
  };

  const handleSync = async () => {
    setActionLoading(true);
    try {
      const result = await syncSkills(authFetch, skills.map(s => ({
        name: s.name, slug: s.slug, version: s.version,
        description: s.description || undefined,
        install_path: s.install_path, module_count: s.module_count,
      })));
      setError(null);
      setSyncResult(`Sync complete: ${result.inserted} inserted, ${result.updated} updated, ${result.unchanged} unchanged`);
      setTimeout(() => setSyncResult(null), 5000);
      await fetchData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Sync failed');
    }
    setActionLoading(false);
  };

  if (loading && skills.length === 0) {
    return <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-secondary)' }}>Loading skills...</div>;
  }

  if (error && skills.length === 0) {
    return <div style={{ padding: 12, background: '#fef2f2', color: '#dc2626', borderRadius: 8 }}>{error}</div>;
  }

  if (skills.length === 0 && !showCreate) {
    return (
      <div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginBottom: 16 }}>
          <button onClick={() => setShowCreate(true)} style={{
            fontSize: 13, padding: '6px 16px', borderRadius: 6,
            border: 'none', fontWeight: 500, cursor: 'pointer',
            background: 'var(--accent-primary, #3b82f6)', color: '#fff',
            display: 'inline-flex', alignItems: 'center', gap: 4,
          }}>
            + Register Skill
          </button>
        </div>
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-secondary)' }}>
          No skills registered. Skills are installed during platform deployment.
        </div>
      </div>
    );
  }

  return (
    <div>
      {error && <div style={{ padding: 12, background: '#fef2f2', color: '#dc2626', borderRadius: 8, marginBottom: 16 }}>{error}</div>}
      {syncResult && (
        <div style={{
          padding: '10px 16px', marginBottom: 12,
          background: 'rgba(34, 197, 94, 0.08)',
          border: '1px solid rgba(34, 197, 94, 0.2)',
          borderRadius: 8, color: '#22c55e', fontSize: 13,
        }}>
          {syncResult}
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        <button onClick={() => setShowCreate(true)} style={{
          fontSize: 13, padding: '6px 16px', borderRadius: 6,
          border: 'none', fontWeight: 500, cursor: 'pointer',
          background: 'var(--accent-primary, #3b82f6)', color: '#fff',
          display: 'inline-flex', alignItems: 'center', gap: 4,
        }}>
          + Register Skill
        </button>
        <button onClick={handleSync} disabled={actionLoading} style={{
          fontSize: 13, padding: '6px 12px', borderRadius: 6,
          border: '1px solid var(--border)', fontWeight: 500, cursor: 'pointer',
          background: 'var(--bg-secondary)', color: 'var(--text-primary)',
          display: 'inline-flex', alignItems: 'center', gap: 4,
        }}>
          Sync Skills
        </button>
        <button onClick={fetchData} style={{
          fontSize: 13, padding: '6px 12px', borderRadius: 6,
          border: '1px solid var(--border)', fontWeight: 500, cursor: 'pointer',
          background: 'var(--bg-secondary)', color: 'var(--text-primary)',
          display: 'inline-flex', alignItems: 'center', gap: 4,
        }}>
          <RefreshIcon size={14} /> Refresh
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 16 }}>
        {skills.map(skill => (
          <div key={skill.id} className="glass-card"
            style={{ padding: 20, cursor: 'pointer', transition: 'border-color 0.15s, box-shadow 0.15s, transform 0.1s' }}
            onClick={() => setExpandedId(expandedId === skill.id ? null : skill.id)}
            onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--accent-primary, #3b82f6)'; e.currentTarget.style.boxShadow = '0 4px 16px rgba(59, 130, 246, 0.1)'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.boxShadow = 'none'; }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
              <h4 style={{ margin: 0, fontSize: 16 }}>{skill.name}</h4>
              <span style={{
                fontSize: 11, padding: '2px 8px', borderRadius: 10,
                background: skill.is_active ? '#dcfce7' : '#f3f4f6',
                color: skill.is_active ? '#166534' : '#6b7280',
              }}>
                {skill.is_active ? 'Active' : 'Inactive'}
              </span>
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 8 }}>
              <span style={{ fontFamily: 'monospace' }}>{skill.slug}</span> &middot; v{skill.version}
            </div>
            {skill.description && (
              <p style={{
                fontSize: 13, color: 'var(--text-secondary)', margin: '0 0 8px',
                overflow: 'hidden', textOverflow: 'ellipsis',
                display: '-webkit-box', WebkitLineClamp: expandedId === skill.id ? 999 : 2,
                WebkitBoxOrient: 'vertical',
              }}>
                {skill.description}
              </p>
            )}
            <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
              {skill.module_count} module{skill.module_count !== 1 ? 's' : ''}
            </div>

            {expandedId === skill.id && (
              <div style={{ marginTop: 12, borderTop: '1px solid var(--border)', paddingTop: 12 }}>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 4 }}>
                  <strong>Path:</strong> {skill.install_path}
                </div>
                {skill.registered_by && (
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 4 }}>
                    <strong>Registered by:</strong> {skill.registered_by}
                  </div>
                )}
                {skill.module_inventory && Object.keys(skill.module_inventory).length > 0 && (
                  <details style={{ marginTop: 8 }}>
                    <summary style={{ cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>Module Inventory</summary>
                    <pre style={{ fontSize: 11, overflow: 'auto', maxHeight: 200, padding: 8, background: 'var(--bg-tertiary)', borderRadius: 6 }}>
                      {JSON.stringify(skill.module_inventory, null, 2)}
                    </pre>
                  </details>
                )}
                <button
                  onClick={async (e) => {
                    e.stopPropagation();
                    try {
                      await updateSkill(authFetch, skill.id, { is_active: !skill.is_active });
                      await fetchData();
                    } catch (err) {
                      setError(err instanceof Error ? err.message : 'Update failed');
                    }
                  }}
                  style={{
                    fontSize: 12, padding: '4px 12px', borderRadius: 6, cursor: 'pointer',
                    border: `1px solid ${skill.is_active ? '#dc262640' : '#22c55e40'}`,
                    background: skill.is_active ? '#dc262610' : '#22c55e10',
                    color: skill.is_active ? '#dc2626' : '#22c55e',
                    marginTop: 8,
                  }}>
                  {skill.is_active ? 'Deactivate' : 'Activate'}
                </button>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Register Skill Modal */}
      {showCreate && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
          onClick={() => setShowCreate(false)}>
          <div className="glass-card" style={{ padding: 24, minWidth: 420, maxWidth: 520 }} onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 16px' }}>Register Skill</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <label style={{ fontSize: 13 }}>
                Name *
                <input type="text" value={createForm.name}
                  onChange={e => setCreateForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="e.g. ML Practitioner Authority"
                  style={{ width: '100%', padding: '6px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-secondary)', marginTop: 4 }} />
              </label>
              <label style={{ fontSize: 13 }}>
                Slug *
                <input type="text" value={createForm.slug}
                  onChange={e => setCreateForm(f => ({ ...f, slug: e.target.value }))}
                  placeholder="e.g. ml-practitioner-authority"
                  style={{ width: '100%', padding: '6px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-secondary)', marginTop: 4, fontFamily: 'monospace' }} />
              </label>
              <div style={{ display: 'flex', gap: 12 }}>
                <label style={{ fontSize: 13, flex: 1 }}>
                  Version
                  <input type="text" value={createForm.version}
                    onChange={e => setCreateForm(f => ({ ...f, version: e.target.value }))}
                    style={{ width: '100%', padding: '6px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-secondary)', marginTop: 4 }} />
                </label>
                <label style={{ fontSize: 13, width: 100 }}>
                  Modules
                  <input type="number" value={createForm.module_count} min={0}
                    onChange={e => setCreateForm(f => ({ ...f, module_count: parseInt(e.target.value) || 0 }))}
                    style={{ width: '100%', padding: '6px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-secondary)', marginTop: 4 }} />
                </label>
              </div>
              <label style={{ fontSize: 13 }}>
                Install Path
                <input type="text" value={createForm.install_path}
                  onChange={e => setCreateForm(f => ({ ...f, install_path: e.target.value }))}
                  placeholder="e.g. /mnt/skills/user/ml-practitioner-authority/"
                  style={{ width: '100%', padding: '6px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-secondary)', marginTop: 4, fontFamily: 'monospace' }} />
              </label>
              <label style={{ fontSize: 13 }}>
                Description
                <textarea value={createForm.description}
                  onChange={e => setCreateForm(f => ({ ...f, description: e.target.value }))}
                  rows={2} placeholder="Brief description of the skill's domain expertise..."
                  style={{ width: '100%', padding: '6px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-secondary)', marginTop: 4, resize: 'vertical' }} />
              </label>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
                <button onClick={() => setShowCreate(false)} style={{
                  fontSize: 13, padding: '6px 12px', borderRadius: 6,
                  border: '1px solid var(--border)', fontWeight: 500, cursor: 'pointer',
                  background: 'var(--bg-secondary)', color: 'var(--text-primary)',
                }}>Cancel</button>
                <button onClick={handleCreate} disabled={actionLoading || !createForm.name.trim() || !createForm.slug.trim()}
                  style={{
                    fontSize: 13, padding: '6px 16px', borderRadius: 6,
                    border: 'none', fontWeight: 500, cursor: 'pointer',
                    background: 'var(--accent-primary, #3b82f6)', color: '#fff',
                  }}>
                  Register
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SkillLibrary;
